/**
 * @file texts and css class names for post color values
 */
import { __ } from 'bits/intl'
import { PostColor } from 'types'

function postColorText({
  postColor,
  isLightColorScheme,
}: {
  postColor: PostColor
  isLightColorScheme: boolean
}): string {
  switch (postColor) {
    case 'red':
      return __('Red')
    case 'orange':
      return __('Orange')
    case 'green':
      return __('Green')
    case 'blue':
      return __('Blue')
    case 'purple':
      return __('Purple')
    default:
      return isLightColorScheme ? __('White') : __('Black')
  }
}

// dark/light post color depends on the wall's colorScheme setting, not user's browser color scheme
function postColorTailwindClass({
  postColor,
  isLightColorScheme,
}: {
  postColor: PostColor
  isLightColorScheme: boolean
}): string {
  switch (postColor) {
    case 'red':
      return isLightColorScheme ? 'bg-old-post-red' : 'bg-old-post-red-dark'
    case 'orange':
      return isLightColorScheme ? 'bg-old-post-orange' : 'bg-old-post-orange-dark'
    case 'green':
      return isLightColorScheme ? 'bg-old-post-green' : 'bg-old-post-green-dark'
    case 'blue':
      return isLightColorScheme ? 'bg-old-post-blue' : 'bg-old-post-blue-dark'
    case 'purple':
      return isLightColorScheme ? 'bg-old-post-purple' : 'bg-old-post-purple-dark'
    default:
      return isLightColorScheme ? 'bg-old-post-light' : 'bg-old-post-dark'
  }
}

const postColorValues: PostColor[] = [null, 'red', 'orange', 'green', 'blue', 'purple']

type RGB = { r: number; g: number; b: number }
type RGBA = RGB & { a: number }

function rgba(r: number, g: number, b: number, a: number): RGBA {
  return { r: r, g: g, b: b, a: a }
}

const OLD_POST_RED = rgba(255, 205, 210, 1) // '#FFCDD2
const OLD_POST_RED_DARK = rgba(183, 28, 28, 1) // '#B71C1C
const OLD_POST_ORANGE = rgba(255, 236, 179, 1) // '#FFECB3
const OLD_POST_ORANGE_DARK = rgba(255, 111, 0, 1) // '#FF6F00
const OLD_POST_GREEN = rgba(200, 230, 201, 1) // '#C8E6C9
const OLD_POST_GREEN_DARK = rgba(27, 94, 32, 1) // '#1B5E20
const OLD_POST_BLUE = rgba(179, 229, 252, 1) // '#B3E5FC
const OLD_POST_BLUE_DARK = rgba(13, 71, 161, 1) // '#0D47A1
const OLD_POST_PURPLE = rgba(225, 190, 231, 1) // '#E1BEE7
const OLD_POST_PURPLE_DARK = rgba(74, 20, 140, 1) // '#4A148C
const OLD_POST_LIGHT = rgba(255, 255, 255, 1) // '#FFFFFF
const OLD_POST_DARK = rgba(33, 33, 33, 1) // '#212121
const DARK_UI_100 = rgba(17, 17, 17, 1) // #111111
const LIGHT_UI_100 = rgba(255, 255, 255, 1) //#FFFFFF
const DARK_UI_200 = rgba(33, 34, 36, 1) // #212224
const LIGHT_UI_200 = rgba(246, 246, 246, 1) //#F6F6F6

function postColorRgb({ postColor, isLightColorScheme }: { postColor: PostColor; isLightColorScheme: boolean }): RGB {
  switch (postColor) {
    case 'red':
      return isLightColorScheme ? OLD_POST_RED : OLD_POST_RED_DARK
    case 'orange':
      return isLightColorScheme ? OLD_POST_ORANGE : OLD_POST_ORANGE_DARK
    case 'green':
      return isLightColorScheme ? OLD_POST_GREEN : OLD_POST_GREEN_DARK
    case 'blue':
      return isLightColorScheme ? OLD_POST_BLUE : OLD_POST_BLUE_DARK
    case 'purple':
      return isLightColorScheme ? OLD_POST_PURPLE : OLD_POST_PURPLE_DARK
    default:
      return isLightColorScheme ? OLD_POST_LIGHT : OLD_POST_DARK
  }
}

/**
 * Calculate ratio based on relative luminance
 * @see https://www.w3.org/TR/WCAG20-TECHS/G17.html
 * @see https://en.wikipedia.org/wiki/Relative_luminance
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/Understanding_Colors_and_Luminance
 * @see https://webaim.org/resources/contrastchecker/ -- tool to calculating ratio for 2 colors
 */
function computeLuminanceRatio(e1: RGB | RGBA, e2: RGB | RGBA) {
  const l1 = (0.2126 * e1.r) / 255 + (0.7152 * e1.g) / 255 + (0.0722 * e1.b) / 255
  const l2 = (0.2126 * e2.r) / 255 + (0.7152 * e2.g) / 255 + (0.0722 * e2.b) / 255
  const ratio = (l1 + 0.05) / (l2 + 0.05)
  return ratio > 1 ? ratio : 1 / ratio
}

function serializeRgbaStringToRgba(rgbaString: string): RGBA {
  const imageBackgroundArray = rgbaString.replace(/(a|b|g|r|\(|\)|\s)/g, '').split(',')
  return {
    r: parseInt(imageBackgroundArray[0], 10),
    g: parseInt(imageBackgroundArray[1], 10),
    b: parseInt(imageBackgroundArray[2], 10),
    a: imageBackgroundArray.length == 4 ? parseInt(imageBackgroundArray[3], 10) : 1,
  }
}

export {
  postColorText,
  postColorTailwindClass,
  postColorValues,
  postColorRgb,
  computeLuminanceRatio,
  serializeRgbaStringToRgba,
  DARK_UI_100,
  LIGHT_UI_100,
  DARK_UI_200,
  LIGHT_UI_200,
}
export type { RGB, RGBA }
